<template>
    <div class="w-full">
        <!-- INITIAL FORM -->
        <div class="w-full" v-if="!videoUploaded && uploadProgress === null">
            <div class="pin absolute bg-tertiary upload-helper text-center flex items-center justify-center rounded-lg" v-if="draggingFiles" @drop.prevent="filesDropped" @dragenter="overDropzone = true" @dragleave="overDropzone = false">
                <div class="dropzone" :class="{over: overDropzone}">
                    <i class="fas fa-file-upload fa-4x block"></i>
                    <h3 class="mt-5 mb-2">Upload Video</h3>
                    <p>Drop your new video file here</p>
                </div>
            </div>
            <p class="mb-5 text-xs px-2">Click the 'Add Video' button, or drag a file on to this section to upload a new video.</p>

            <div v-for="(video, i) in media" :key="i">
                <div class="flex flex-wrap">
                    <div class="w-full mb-5 px-2">
                        <div class="float-right">
                            <i class="fas fa-arrow-up ml-3 cursor-pointer" title="Move Up" @click="moveUp(video)" v-if="i > 0"></i>
                            <i class="fas fa-arrow-down ml-3 cursor-pointer" title="Move Down" @click="moveDown(video)" v-if="i < media.length - 1"></i>
                            <i class="fas fa-trash-alt ml-3 cursor-pointer" title="Delete" @click="deleteVideo(video)"></i>
                        </div>
                        <label for="ref">Video URL:</label>
                        <input type="text" id="ref" v-model="video.url" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
                    </div>
                </div>

                <div class="flex flex-wrap" v-if="video.url && video.url.indexOf('http') === 0">
                    <div class="w-full mb-5 px-2">
                        <iframe :src="video.url" class="w-full video-preview"></iframe>
                    </div>
                </div>

                <hr class="border-b my-5" v-if="i < media.length - 1" />
            </div>

            <div class="flex flex-wrap">
                <div class="w-full px-2 text-center">
                    <label>
                        <div class="inline-block bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow cursor-pointer">
                            + Add Video
                        </div>
                        <input type="file" class="hidden" @change="handleFileUpload" accept="video/*" multiple/>
                    </label>
                </div>
            </div>
        </div>

        <!-- UPLOADING -->
        <div class="w-full" v-if="!videoUploaded && uploadProgress !== null">
            <h3 class="mb-5">Uploading Video...</h3>
            <p class="mb-5">{{ filename }}</p>
            <p class="text-right">{{ uploadProgress }}%</p>
            <div class="border p-2 mb-5">
                <div class="bg-tertiary progress" :style="{width: `${uploadProgress}%`}"></div>
            </div>
        </div>

        <!-- UPLOADED -->
        <div class="w-full" v-if="videoUploaded">
            <h3 class="mb-5">Upload Complete</h3>
            <p>We've received your video and we're now uploading it to Vimeo. The video will automatically attach to this property when the process completes.</p>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import api from '@/http'
import http from 'axios'

export default {
  name: 'cms-video',
  props: {
    media: Array
  },
  data () {
    return {
      draggingFiles: false,
      overDropzone: false,
      uploadProgress: null,
      videoUploaded: false,
      filename: null
    }
  },
  computed: {
    ...mapState(['currentProperty', 'selectedOffice'])
  },
  created () {
    document.addEventListener('dragover', this.dragAndDropStart)
    document.addEventListener('dragleave', this.dragAndDropEnd)
    document.addEventListener('drop', this.dragAndDropEnd)
  },
  beforeDestroy () {
    document.removeEventListener('dragover', this.dragAndDropStart)
    document.removeEventListener('dragleave', this.dragAndDropEnd)
    document.removeEventListener('drop', this.dragAndDropEnd)
  },
  methods: {
    ...mapActions(['displayToastNotification']),
    dragAndDropStart (e) {
      e.preventDefault()
      this.draggingFiles = true
    },
    dragAndDropEnd (e) {
      e.preventDefault()
      this.draggingFiles = false
    },
    filesDropped (e) {
      this.draggingFiles = false
      this.overDropzone = false

      if (e.dataTransfer.items) {
        for (let i = 0; i < e.dataTransfer.items.length; i++) {
          if (e.dataTransfer.items[i].kind !== 'file') {
            continue
          }

          this.uploadFile(e.dataTransfer.items[i].getAsFile())
        }
      } else {
        for (let i = 0; i < e.dataTransfer.files.length; i++) {
          this.uploadFile(e.dataTransfer.files[i])
        }
      }
    },
    handleFileUpload (e) {
      for (let i = 0; i < e.target.files.length; i++) {
        this.uploadFile(e.target.files[i])
      }
    },
    async uploadFile (file) {
      // Validate
      if (file.type.indexOf('video/') !== 0) {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Upload Failed',
          info: `${file.name} is not a valid format`
        })
        return
      }
      this.filename = file.name

      let filePath = `properties/${this.selectedOffice.network_id}/${this.selectedOffice.client_id}/${this.selectedOffice.id}/${this.currentProperty.id}`

      const extension = file.name.split('.').pop()
      const response = await api.post('signed-upload', {
        'disk': 's3',
        'extension': extension,
        'path': filePath
      })

      let s3File = response.data.key
      let headers = response.data.headers
      if ('Host' in headers) {
        delete headers.Host
      }

      await http.put(response.data.url, file, {
        headers: headers,
        onUploadProgress: (progressEvent) => {
          this.uploadProgress = Math.ceil(progressEvent.loaded / progressEvent.total * 100)
        }
      })

      api.post(`/properties/${this.currentProperty.id}/upload-video`, {
        filePath: s3File,
        extension: extension
      })
        .then((response) => {
          this.videoUploaded = true
          this.currentProperty.video_uploaded = true
        }, () => {
          this.uploadProgress = null
          this.videoUploaded = false
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-times-circle',
            title: 'Upload Failed',
            info: file.name
          })
        })
    },
    moveUp (video) {
      let index = this.media.indexOf(video)
      this.media.splice(index, 1)
      this.media.splice(index - 1, 0, video)
    },
    moveDown (video) {
      let index = this.media.indexOf(video)
      this.media.splice(index, 1)
      this.media.splice(index + 1, 0, video)
    },
    deleteVideo (video) {
      let index = this.media.indexOf(video)
      this.media.splice(index, 1)
    }
  }
}
</script>

<style lang="scss" scoped>
.upload-helper {
    background-color: rgba(255,255,255,.95);
    z-index: 2;
}
.dropzone {
    transform: scale(1);
    transition: transform .3s;
    pointer-events: none;
}
.dropzone.over {
    transform: scale(1.1)
}
.progress {
    height: 20px;
}
.video-preview {
    height: 160px;
}
</style>
